import { gql, type TypedDocumentNode } from '@apollo/client';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Collect } from '@sorare/core/src/atoms/icons/Collect';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift.ts';
import { HalfCircularProgressBar } from '@sorare/core/src/atoms/loader/CircularProgressIndicator/HalfCircularProgressBar/index.tsx';
import { LabelXS } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_SERIES_MISSIONS_COLLECT } from '@sorare/core/src/constants/__generated__/routes.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { IconWrapper, MissionButton } from '../ui.tsx';
import type { FeaturedCollectionTaskWidget_taskInterface } from './__generated__/index.graphql.ts';

const Gift = styled.span`
  background: var(--c-blue-400);
  border-radius: 16px;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 50%);
  padding: 2px var(--unit);
`;

type Props = {
  featuredCollectionTask: Extract<
    FeaturedCollectionTaskWidget_taskInterface,
    { __typename: 'CardsCountTask' | 'CompleteCollectionsCountTask' }
  >;
  hasClaimableCollectionTasks: boolean;
};
export const FeaturedCollectionTaskWidget = ({
  featuredCollectionTask,
  hasClaimableCollectionTasks,
}: Props) => {
  const { generateSportContextPath } = useSportContext();

  return (
    <MissionButton
      to={generateSportContextPath(ANY_SPORT_SERIES_MISSIONS_COLLECT)}
    >
      <HalfCircularProgressBar
        size={6}
        progresses={[
          hasClaimableCollectionTasks
            ? 1
            : featuredCollectionTask.liveScore /
              (featuredCollectionTask.scoreToReach || 1),
        ]}
        colors={[
          !hasClaimableCollectionTasks
            ? {
                from: 'var(--c-set-surface-primary)',
                to: 'var(--c-set-surface-primary)',
              }
            : {
                from: 'var(--c-blue-400)',
                to: 'var(--c-blue-400)',
              },
        ]}
      />
      <IconWrapper>
        <Collect size={4} colors={['#E100FF', '#022FF2']} />
      </IconWrapper>
      {!!hasClaimableCollectionTasks && (
        <Gift>
          <LabelXS>
            <FontAwesomeIcon icon={iconGift} />
          </LabelXS>
        </Gift>
      )}
    </MissionButton>
  );
};

FeaturedCollectionTaskWidget.fragments = {
  taskInterface: gql`
    fragment FeaturedCollectionTaskWidget_taskInterface on TaskInterface {
      id
      ... on CardsCountTask {
        id
        liveScore
        scoreToReach
      }
      ... on CompleteCollectionsCountTask {
        id
        liveScore
        scoreToReach
      }
    }
  ` as TypedDocumentNode<FeaturedCollectionTaskWidget_taskInterface>,
};
